import { Flex } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router';
import type { Initiative } from '../../../api/initiative-client/initiative-client.type';
import { InitiativeSource, InitiativeStatus } from '../../../api/initiative-client/initiative-client.type';
import { useUpdateGoalMutation, useUpdateOutcomeMutation } from '../../../api/strategy-client/strategy-client.hooks';
import type { Goal, Outcome } from '../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { baseWhite, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { toast } from '../../notifications';
import { getSourceIcon, getStatusColor } from '../initiative-list/main-section/main-section.helpers';
import { StrategyEntityModal } from '../strategy-entity-modal/strategy-entity-modal';
import { StrategyEntityType } from '../strategy.type';

type Props = {
  entity: Initiative | Outcome | Goal | null;
  entityType: StrategyEntityType;
  onUpdate?: () => void;
};

export const StrategyPerformanceHeader = ({ entity, entityType, onUpdate }: Props) => {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  const updateOutcomeMutation = useUpdateOutcomeMutation({
    onSuccess: (data) => {
      closeModal();
      toast.success(`${i18n.t('common.outcome')} "${data.name}" updated successfully`);
      if (onUpdate) {
        onUpdate();
      }
    },
    onError: (error) => {
      toast.error(
        `Failed to update ${i18n.t('common.outcome').toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    },
  });

  const updateGoalMutation = useUpdateGoalMutation({
    onSuccess: (data) => {
      closeModal();
      toast.success(`${i18n.t('common.strategic_goal')} "${data.name}" updated successfully`);
      if (onUpdate) {
        onUpdate();
      }
    },
    onError: (error) => {
      toast.error(
        `Failed to update ${i18n.t('common.strategic_goal').toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    },
  });

  const getBackRoute = () => {
    switch (entityType) {
      case StrategyEntityType.Goal:
        return '/application/strategy/goals';
      default:
        return '/application/strategy';
    }
  };

  const getBackLabel = () => {
    switch (entityType) {
      case StrategyEntityType.Goal:
        return `Back to ${i18n.t('common.strategic_goals')}`;
      case StrategyEntityType.Outcome:
        return i18n.t('common.actions.back');
      default:
        return i18n.t('strategy.initiatives.back');
    }
  };

  const canEdit = entity && entity.source === 'bloomfilter' && entityType !== StrategyEntityType.Initiative;

  const handleUpdate = async (data: any) => {
    if (!entity) return;

    if (entityType === StrategyEntityType.Outcome) {
      await updateOutcomeMutation.mutateAsync({ outcomeId: entity.id, data });
    } else if (entityType === StrategyEntityType.Goal) {
      await updateGoalMutation.mutateAsync({ goalId: entity.id, data });
    }
  };

  const handleBackClick = () => {
    if (entityType === StrategyEntityType.Outcome) {
      // Check if history is safe to navigate back
      const hasHistory = (window.history.state?.idx ?? window.history.length) > 1;
      if (hasHistory) {
        navigate(-1);
      } else {
        navigate('/application/strategy');
      }
    } else {
      navigate(getBackRoute());
    }
  };

  return (
    <>
      <Flex align="center" gap={16}>
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={<Icon name="arrow_back" size={18} color={baseWhite} />}
          onClick={handleBackClick}
        >
          {getBackLabel()}
        </Button>

        {entity && (
          <Flex align="center" gap={8}>
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: getStatusColor((entity as any).status?.by_tasks?.status || InitiativeStatus.Unknown),
              }}
            />
            <Text size="large" weight="medium" color={secondaryBase}>
              {entity.key || 'Unknown Key'}
            </Text>
            <img
              src={getSourceIcon(entity.source as InitiativeSource)}
              width={24}
              height={24}
              alt={String(entity.source)}
            />
            {'url' in entity && entity.url && (
              <Tooltip variant="white" label={i18n.t('common.visit_source_page')} position="right" zIndex={1000}>
                <Icon
                  name="arrow_outward"
                  size={24}
                  color={secondaryBase}
                  pointer
                  onClick={() => {
                    if ('url' in entity && entity.url) {
                      window.open(entity.url, '_blank', 'noopener, noreferrer');
                    }
                  }}
                />
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip variant="white" label="Edit" position="right" zIndex={1000}>
                <Icon name="edit" size={24} color={secondaryBase} pointer onClick={openModal} />
              </Tooltip>
            )}
          </Flex>
        )}
      </Flex>

      {canEdit && entity && (
        <StrategyEntityModal
          mode="edit"
          entityType={entityType}
          entity={entity}
          opened={modalOpened}
          onClose={closeModal}
          onSubmit={handleUpdate}
          isSubmitting={updateOutcomeMutation.isPending || updateGoalMutation.isPending}
        />
      )}
    </>
  );
};
